// src/pages/Employees.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Nav } from '../../components/nav';
import { useAuth } from '../../navigation/AuthProvider';
import {
  delete_employee,
  edit_employee,
  deactivate_employee,
  reactivate_employee  
} from '../../utils/employeeRequests';
import '../css/Employees.css';
import { Toast } from '../../components/ToastPopup';
import { BACKEND_URL } from '../../constants';
import axios from 'axios';
import { calculatePayForEmployee } from '../../utils/calculatePayForEmployee';
import * as XLSX from 'xlsx';
import { getPermission } from '../../permissions/rolePermissions';

import { FaEllipsisH, FaPlus } from "react-icons/fa";
import { SiGooglesheets } from "react-icons/si";

import ImportEmployeesPopup from '../../components/imports/ImportEmployeesPopup';

export const Employees = () => {
  const {
    company,
    setCompany,
    employees,
    setEmployees,
    draftSchedule,
    user,
    usersHours,
    setProjects,
    setDraftSchedule,
    timeSchemas,
    setLoading,
    obRules,
    overtimeRules,
    absenceRules,
    vacationPay,
    pays
  } = useAuth();

  const employeesPermission = getPermission(user.role, "employees");
  const canEditEmployees = employeesPermission === "edit";


  const isSuperAdmin = user?.role === "super-admin";
  const isAdmin = user?.role === "admin";

  // Toast
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  // Data & filter
  const [allEmployees, setAllEmployees] = useState(employees || []);
  const [allUsersHours, setAllUsersHours] = useState(usersHours || []);
  const [allShifts, setAllShifts] = useState(draftSchedule || []);
  const [allTimeSchemas, setAllTimeSchemas] = useState(timeSchemas || []);
  const [selectedTimeSchema, setSelectedTimeSchema] = useState(null);

  // Sök
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState(allEmployees);

  // ➕ NYTT tidigare: status-filter
  const [inviteFilter, setInviteFilter] = useState('all');

  // Ny anställd / redigera
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newEmployeeEmail, setNewEmployeeEmail] = useState('');
  const [newEmployeeRole, setNewEmployeeRole] = useState('');
  const [newEmployeeTitle, setNewEmployeeTitle] = useState(''); // Arbetsroll (valfri)
  const [newEmployeePersonalNumber, setNewEmployeePersonalNumber] = useState(''); // Personnummer (valfritt)

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editEmployeeState, setEditEmployeeState] = useState(null);

  // ⏳ Prevent double-submits in popup (invite + edit)
  const [formSubmitting, setFormSubmitting] = useState(false);

  // År & månad
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Action-meny per rad
  const [activeMenu, setActiveMenu] = useState(null);
  const menuRef = useRef(null);

  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showImportEmployeesPopup, setShowImportEmployeesPopup] = useState(false);

  // Resend-modal
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [employeeToResend, setEmployeeToResend] = useState(null);

  // Delete-modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [employeeToDeactivate, setEmployeeToDeactivate] = useState(null);
  const [deactivatingEmployee, setDeactivatingEmployee] = useState(false);
  const [reactivateLoadingId, setReactivateLoadingId] = useState(null);

  // ⏳ Prevent double-click on delete confirm
  const [deletingEmployee, setDeletingEmployee] = useState(false);

  // ⏳ Per-button loaders for reminder/resend
  const [loadingReminderId, setLoadingReminderId] = useState(null);
  const [loadingResendId, setLoadingResendId] = useState(null);

  const [payLoading, setPayLoading] = useState(false);
    const ROLE_LABELS_SV = {
    "super-admin": "Super-Admin",
    "admin": "Admin",
    "scheduler": "Schemaläggare",
    "payroll-manager": "Lönehanterare",
    "viewer": "Granskare",
    "user": "Användare",
  };

  const monthsInSwedish = [
    'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
    'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
  ];

  // Visuell sortering: super-admin → admin → user
  const rolePriority = {
    "super-admin": 1,
    "admin": 2,
    "scheduler": 3,
    "payroll-manager": 4,
    "viewer": 5,
    "user": 6,
  };


  // ✅ Enda ändringen: kräver nu även status === "complete"
  const isShiftCompleteForPayroll = (shift) => {
    return !!(
      shift &&
      shift.status === "complete" &&
      shift.date &&
      typeof shift.startTime === "string" && shift.startTime.includes(":") &&
      typeof shift.endTime === "string" && shift.endTime.includes(":") &&
      Array.isArray(shift.employees) &&
      shift.employees.some(e => !!e?.employeeId)
    );
  };

  const employeesWithShiftsInSchema = useMemo(() => {
    if (!selectedTimeSchema) return allEmployees;

    return allEmployees.filter(emp =>
      allShifts.some(shift =>
        isShiftCompleteForPayroll(shift) &&
        shift.timeSchema === selectedTimeSchema &&
        shift.employees.some(e => e.employeeId === emp._id)
      )
    );
  }, [selectedTimeSchema, allShifts, allEmployees]);

  const workedHoursMap = useMemo(() => {
    const map = {};

    allEmployees.forEach(emp => {
      const selectedShifts = allShifts.filter(shift => {
        if (!isShiftCompleteForPayroll(shift)) return false;
        const date = new Date(shift.date);
        const monthMatch  = date.getMonth() + 1 === selectedMonth;
        const yearMatch   = date.getFullYear() === selectedYear;
        const isAssigned  = shift.employees.some(e => e.employeeId === emp._id);
        const schemaMatch = !selectedTimeSchema || shift.timeSchema === selectedTimeSchema;
        return monthMatch && yearMatch && isAssigned && schemaMatch;
      });

      const summary = calculatePayForEmployee({
        employee: emp,
        shifts: selectedShifts,
        payRates: pays || [],
        vacationPay: vacationPay || {},
        overtimeRules: overtimeRules || [],
        obRules: obRules || [],
        absenceRules: absenceRules || [],
      });

      map[emp._id] = summary.totalHours.toFixed(2);
    });

    return map;
  }, [allEmployees, allShifts, selectedMonth, selectedYear, selectedTimeSchema, company, pays, vacationPay, overtimeRules, obRules, absenceRules]);

  // Excel-export (behåller befintlig ordning)
  const handleDownloadExcel = () => {
    const dataForExcel = filteredEmployees.map(emp => ({
      Namn: `${emp.name} ${emp.lastName || ''}`.trim(),
      Arbetsroll: emp.title || "",
      Personnummer: emp.personalNumber || "",
      Email: emp.email,
      Timmar: workedHoursMap[emp._id] || "0.00",
      Roll: emp.role
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Anställda");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `anstallda_${selectedYear}-${String(selectedMonth).padStart(2, "0")}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };



  // --- EFFECTS ---
  useEffect(() => {
    setAllEmployees(employees || []);
    setFilteredEmployees(employees || []);
  }, [employees]);

  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();

    const updated = (employeesWithShiftsInSchema || [])
      .filter(emp => {
        // ----- sökfilter -----
        const name = `${emp.name} ${emp.lastName || ''}`.toLowerCase();
        const title = (emp.title || '').toLowerCase();
        const pn = (emp.personalNumber || '').toLowerCase();
        const email = (emp.email || '').toLowerCase();
        const matchesSearch = !q || name.includes(q) || title.includes(q) || pn.includes(q) || email.includes(q);

        if (!matchesSearch) return false;

        // ----- status-filter -----
        if (inviteFilter !== 'all') {
          const expiresAt = emp.invitationExpiresAt ? new Date(emp.invitationExpiresAt) : null;

          const isPendingNow = emp.membershipStatus === "pending" && (!expiresAt || expiresAt.getTime() > Date.now());
          const isExpiredNow = emp.membershipStatus === "pending" && !!expiresAt && expiresAt.getTime() <= Date.now();
          const isActiveNow  = emp.membershipStatus === "active" && emp.isActive !== false;
          const isInactiveNow = emp.isActive === false;

          if (inviteFilter === 'invited' && !isPendingNow) return false;
          if (inviteFilter === 'expired' && !isExpiredNow) return false;
          if (inviteFilter === 'active'  && !isActiveNow)  return false;
          if (inviteFilter === 'inactive' && !isInactiveNow) return false;
        }

        return true;
      })
      .sort((a, b) => rolePriority[a.role] - rolePriority[b.role]);

    setFilteredEmployees(updated);
  }, [searchQuery, employeesWithShiftsInSchema, inviteFilter]);

  // Hämta månadens skift (behåller din tidigare logik)
  useEffect(() => {
    const hasInitialShifts = allShifts.some(shift => {
      const d = new Date(shift.date);
      return d.getMonth() + 1 === selectedMonth &&
             d.getFullYear() === selectedYear;
    });

    if (hasInitialShifts) return;

    const fetchShifts = async () => {
      setPayLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/get-shifts-for-month`, {
          params: {
            userId: user._id,
            companyId: company._id,
            selectedMonth,
            selectedYear,
          },
        });
        setAllShifts(data.shifts || []);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch shifts:", err);
      }
      setPayLoading(false);
    };

    fetchShifts();
  }, [selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (type, value) => {
    const v = parseInt(value, 10);
    if (type === "year") setSelectedYear(v);
    if (type === "month") setSelectedMonth(v);
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (formSubmitting) return;
    setFormSubmitting(true);


    if (user.role === "user") {
      setToastType("error");
      setToastMessage("Vanliga användare kan inte redigera användare!");
      setFormSubmitting(false);
      return;
    }

    // Redigera anställd
    if (editEmployeeState) {
      try {
        const editedFields = {
          name: newEmployeeName,
          role: newEmployeeRole,
          title: newEmployeeTitle || "",
          personalNumber: (newEmployeePersonalNumber || "").trim() || "",
        };

        // Skicka bara email-fältet om det faktiskt ändrats
        if (
          typeof newEmployeeEmail === "string" &&
          newEmployeeEmail.trim().toLowerCase() !==
            String(editEmployeeState.email || "").trim().toLowerCase()
        ) {
          editedFields.email = newEmployeeEmail.trim();
        }

        const data = await edit_employee({
          user,
          company,
          employeeToEditId: editEmployeeState._id,
          editedFields
        });

        if (data?.status === "success") {
          setEmployees(data.employees);
          setNewEmployeeName("");
          setNewEmployeeEmail("");
          setNewEmployeeRole("");
          setNewEmployeeTitle("");
          setNewEmployeePersonalNumber("");
          setIsPopupOpen(false);
          setToastType("success");
          setToastMessage("Användare redigerad!");
        } else {
          setToastType("error");
          setToastMessage(data?.errorText || "Kunde inte redigera användaren.");
        }
      } finally {
        setFormSubmitting(false);
      }
      return;
    }

    // Lägg till ny anställd
    try {
      const body = {
        current_users_id: user._id,
        companyId: company._id,
        newUser: {
          name: newEmployeeName,
          email: newEmployeeEmail,
          role: newEmployeeRole,
          title: (newEmployeeTitle || "").trim() || undefined,
          personalNumber: (newEmployeePersonalNumber || "").trim() || "",
        },
      };

      const { data } = await axios.post(`${BACKEND_URL}/add-new-employee`, body);

      if (!data || data.status === 'failure') {
        setToastType("error");
        setToastMessage(data?.errorText || "Kunde inte lägga till användaren.");
        setFormSubmitting(false);
        return;
      }

      setEmployees(data.employees);
      setAllEmployees(data.employees);
      setFilteredEmployees(data.employees);
      setToastType("success");
      setToastMessage(`Inbjudan skickad till ${newEmployeeName} via email`);
      setNewEmployeeName('');
      setNewEmployeeEmail('');
      setNewEmployeeRole('');
      setNewEmployeeTitle('');
      setNewEmployeePersonalNumber('');
      setIsPopupOpen(false);
    } catch (err) {
      setToastType("error");
      setToastMessage("Kunde inte lägga till användaren (nätverksfel).");
    } finally {
      setFormSubmitting(false);
    }
  };

  const openDeleteModal = emp => {
    setEmployeeToDelete(emp);
    setDeleteModalOpen(true);
    setActiveMenu(null);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setEmployeeToDelete(null);
    setDeleteConfirmText('');
  };

  const confirmDelete = async () => {
    if (deletingEmployee) return;
    setDeletingEmployee(true);

    setLoading(true);
    const data = await delete_employee({
      employeeToRemoveId: employeeToDelete._id,
      company,
      user
    });
    if (!data || data.status === "failure") {
      await setLoading(false);
      setToastType("error");
      setToastMessage("Fel inträffade: " + (data?.errorText || ""));
      setDeletingEmployee(false);
    } else {
      setCompany(data.company);
      setEmployees(data.employees);
      setAllEmployees(data.employees);
      setFilteredEmployees(data.employees);
      setProjects(data.projects);
      setDraftSchedule(data.shifts);
      await setLoading(false);
      setDeletingEmployee(false);
      window.location.reload();
    }
    closeDeleteModal();
  };

  // tiny inline spinner for menu buttons
  const InlineSpinner = () => (
    <svg width="14" height="14" viewBox="0 0 50 50" style={{ marginRight: 8 }} aria-hidden="true">
      <circle cx="25" cy="25" r="20" strokeWidth="6" stroke="currentColor" fill="none" opacity="0.25" />
      <circle cx="25" cy="25" r="20" strokeWidth="6" stroke="currentColor" fill="none" strokeDasharray="90,150">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.75s" repeatCount="indefinite" />
      </circle>
    </svg>
  );

  const handleResendInvite = async (emp) => {
    if (loadingResendId || loadingReminderId) return; // avoid overlapping clicks
    setLoadingResendId(emp._id);
    try {
      const body = {
        current_users_id: user._id,
        companyId: company._id,
        newUser: {
          name: emp.name,
          email: emp.email,
          role: emp.role,
          title: (emp.title || "").trim() || "",
          personalNumber: (emp.personalNumber || "").trim() || "",
        },
      };

      const { data } = await axios.post(`${BACKEND_URL}/add-new-employee`, body);

      if (data?.status === "success") {
        setToastType("success");
        setToastMessage(`Inbjudan skickad till ${emp.name} via email`);
        if (data.employees) {
          setEmployees(data.employees);
          setAllEmployees(data.employees);
          setFilteredEmployees(data.employees);
        }
      } else {
        setToastType("error");
        setToastMessage(`Kunde inte skicka till ${emp.name}: ${data?.errorText || "Okänt fel"}`);
      }
    } catch {
      setToastType("error");
      setToastMessage(`Kunde inte skicka till ${emp.name}: nätverksfel`);
    } finally {
      setLoadingResendId(null);
    }
  };

  // Skicka påminnelse
  const handleSendReminder = async (emp) => {
    if (loadingResendId || loadingReminderId) return;
    setLoadingReminderId(emp._id);
    try {
      const body = {
        current_users_id: user._id,
        companyId: company._id,
        employeeId: emp._id,
      };

      const { data } = await axios.post(`${BACKEND_URL}/send-invitation-reminder`, body);

      if (data?.status === "success") {
        setToastType("success");
        setToastMessage(`Påminnelse skickad till ${emp.name}.`);
        if (data.employees) {
          setEmployees(data.employees);
          setAllEmployees(data.employees);
          setFilteredEmployees(data.employees);
        }
      } else {
        setToastType("error");
        setToastMessage(data?.errorText || "Kunde inte skicka påminnelsen.");
      }
    } catch {
      setToastType("error");
      setToastMessage("Kunde inte skicka påminnelse (nätverksfel).");
    } finally {
      setLoadingReminderId(null);
    }
  };

  const openResendModal = emp => {
    setEmployeeToResend(emp);
    setResendModalOpen(true);
  };

  const closeResendModal = () => {
    setResendModalOpen(false);
    setEmployeeToResend(null);
  };

  const confirmResend = async () => {
    await handleResendInvite(employeeToResend);
    closeResendModal();
  };




  const openDeactivateModal = (emp) => {
    setEmployeeToDeactivate(emp);
    setDeactivateModalOpen(true);
    setActiveMenu(null);
  };

  const closeDeactivateModal = () => {
    setDeactivateModalOpen(false);
    setEmployeeToDeactivate(null);
  };

  const confirmDeactivate = async () => {
    if (deactivatingEmployee) return;
    setDeactivatingEmployee(true);

    try {
      const data = await deactivate_employee({
        employeeId: employeeToDeactivate._id,
        company,
        user
      });

      if (!data || data.status === "failure") {
        setToastType("error");
        setToastMessage(data?.errorText || "Kunde inte avaktivera.");
      } else {
        setEmployees(data.employees);
        setAllEmployees(data.employees);
        setFilteredEmployees(data.employees);
        setToastType("success");
        setToastMessage("Användare avaktiverad");
      }
    } catch {
      setToastType("error");
      setToastMessage("Nätverksfel");
    }

    setDeactivatingEmployee(false);
    closeDeactivateModal();
  };

  const handleReactivate = async (emp) => {
    if (reactivateLoadingId) return;

    setReactivateLoadingId(emp._id);

    try {
      const data = await reactivate_employee({
        employeeId: emp._id,
        company,
        user
      });

      if (!data || data.status === "failure") {
        setToastType("error");
        setToastMessage(data?.errorText || "Kunde inte återaktivera.");
      } else {
        setEmployees(data.employees);
        setAllEmployees(data.employees);
        setFilteredEmployees(data.employees);
        setToastType("success");
        setToastMessage(`${emp.name} är nu aktiv igen`);
      }
    } catch {
      setToastType("error");
      setToastMessage("Nätverksfel");
    }

    setReactivateLoadingId(null);
  };


  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
    setEditEmployeeState(null);
    setNewEmployeeName('');
    setNewEmployeeEmail('');
    setNewEmployeeRole('');
    setNewEmployeeTitle('');
    setNewEmployeePersonalNumber('');
    setFormSubmitting(false);
  };

  const handleEdit = emp => {
    setActiveMenu(null);
    setEditEmployeeState(emp);
    setNewEmployeeName(emp.name);
    setNewEmployeeEmail(emp.email);
    setNewEmployeeRole(emp.role);
    setNewEmployeeTitle(emp.title || '');
    setNewEmployeePersonalNumber(emp.personalNumber || '');
    setIsPopupOpen(true);
    setFormSubmitting(false);
  };

  const toggleMenu = id => {
    setActiveMenu(activeMenu === id ? null : id);
  };

  const Spinner = () => (
    <div className="spinner-container">
      <div className="spinner" />
    </div>
  );

  // ✅ Uppdaterad grid-layout: Namn | Email | Personnummer | Arbetsroll | Timmar | Systemroll | actions
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1.2fr 2fr 1.5fr 1fr 0.8fr 0.8fr auto',
    gap: '8px',
    alignItems: 'center'
  };
  const smallText = { fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0 };

  // 🔒 Roll-låsning för super-admin
  const isEditingSuperAdmin = !!editEmployeeState && editEmployeeState.role === 'super-admin';

  // Kan vi ändra e-post i popupen? (endast pending = inbjuden/utgången)
  const canEditEmailInPopup =
    !!editEmployeeState && editEmployeeState.membershipStatus === "pending";

  return (
    <div className="employees-page">
      <Nav />

      <div className="page-header">
        <h1>Anställda</h1>
        {canEditEmployees && (
          <>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
              
              {/* ADD EMPLOYEE */}
              <button
                className="add-button"
                onClick={togglePopup}
                style={{ height: 40, padding: "0 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}
              >
                <FaPlus style={{ fontSize: 11 }} />
                Lägg till anställd
              </button>

              {/* MORE BUTTON */}
              <button
                onClick={() => setShowImportMenu((prev) => !prev)}
                style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid #e5e7eb", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#374151", fontSize: 14 }}
              >
                <FaEllipsisH />
              </button>

              {/* DROPDOWN */}
              {showImportMenu && (
                <div style={{ position: "absolute", top: 46, right: 0, background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.12)", padding: 6, minWidth: 220, zIndex: 99999 }}>
                  
                  {/* IMPORT */}
                  <div
                    onClick={() => {
                      setShowImportMenu(false);

                      setShowImportEmployeesPopup(true);
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", color: "#111827", userSelect: "none" }}
                  >
                    <SiGooglesheets style={{ fontSize: 15 }} />
                    Importera anställda
                  </div>

                  {/* EXPORT */}
                  <div
                    onClick={() => {
                      setShowImportMenu(false);
                      handleDownloadExcel();
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer", color: "#111827", userSelect: "none" }}
                  >
                    <SiGooglesheets style={{ fontSize: 15 }} />
                    Exportera Excel
                  </div>
                </div>
              )}
            </div>
          </>
        )}

      </div>

      <div className="filter-area">
        <input
          type="text"
          className="search-input"
          placeholder="Sök på namn, email, arbetsroll eller personnummer..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />

        {/* Inbjudning - label + select på samma rad */}
        <div style={{display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap'}}>

          <select
            className="month-select"
            value={inviteFilter}
            onChange={e => setInviteFilter(e.target.value)}
            style={{ fontSize: "13px", padding: "6px 12px", borderRadius: "6px", border: "1px solid #ccc", backgroundColor: "#fff" }}
          >
            <option value="all">Alla användare</option>
            <option value="active">Aktiva</option>
            <option value="inactive">Inaktiverade (Grå-markerade)</option>
            <option value="invited">Inbjudna (Gul-markerade)</option>
            <option value="expired">Utgångna (Röd-markerade)</option>
          </select>
        </div>

        {/* Schema - label + select på samma rad */}
        <div style={{display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap'}}>
        
          <select
            className="month-select"
            style={{ fontSize: "13px", padding: "6px 12px", borderRadius: "6px", border: "1px solid #ccc", backgroundColor: "#fff" }}
            value={selectedTimeSchema || ""}
            onChange={e => setSelectedTimeSchema(e.target.value)}
          >
            <option value="">Alla scheman</option>
            {allTimeSchemas.map((timeSchema) => (
              <option key={timeSchema._id} value={timeSchema._id}>
                {timeSchema.name}
              </option>
            ))}
          </select>
        </div>

        <select
          className="month-select"
          value={selectedYear}
          onChange={e => handleDateChange("year", e.target.value)}
        >
          {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + 1 - i).map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select
          className="month-select"
          value={selectedMonth}
          onChange={e => handleDateChange("month", e.target.value)}
        >
          {monthsInSwedish.map((m, i) => (
            <option key={i} value={i + 1}>{m}</option>
          ))}
        </select>
      </div>

      {/* Popup för att lägga till/redigera anställd */}
      {isPopupOpen && (
        <div className="popup-overlay">
          <div className="popup" style={{ borderRadius: 10, padding: 20 }}>
            <h4 style={{ marginTop: 0 }}>{editEmployeeState ? 'Redigera Anställd' : 'Lägg till Ny Anställd'}</h4>
            <form onSubmit={handleSubmit} className="employee-form" style={{ display: 'grid', gap: 10 }}>
              <input
                type="text"
                value={newEmployeeName}
                onChange={e => setNewEmployeeName(e.target.value)}
                placeholder="Namn"
                required
                style={{ padding: 10, borderRadius: 6, border: '1px solid #D1D5DB' }}
              />
              <input
                type="email"
                value={newEmployeeEmail}
                onChange={(e) => setNewEmployeeEmail(e.target.value)}
                placeholder="Email"
                required
                disabled={!!editEmployeeState && !canEditEmailInPopup}
                title={
                  editEmployeeState && !canEditEmailInPopup
                    ? "E-post kan bara ändras för inbjudna/utgångna användare"
                    : undefined
                }
                style={{
                  padding: 10,
                  borderRadius: 6,
                  border: '1px solid',
                  borderColor:
                    editEmployeeState && !canEditEmailInPopup ? "#E5E7EB" : "#D1D5DB",
                  background:
                    editEmployeeState && !canEditEmailInPopup ? "#F9FAFB" : "#FFFFFF",
                  color:
                    editEmployeeState && !canEditEmailInPopup ? "#6B7280" : "#111827",
                  cursor:
                    editEmployeeState && !canEditEmailInPopup ? "not-allowed" : "text",
                }}
              />

              <select
                value={newEmployeeRole}
                onChange={e => setNewEmployeeRole(e.target.value)}
                required
                style={{ padding: 10, borderRadius: 6, border: '1px solid #D1D5DB' }}
                disabled={isEditingSuperAdmin}
                title={isEditingSuperAdmin ? "Super Admin kan inte ändras" : undefined}
              >
                <option value="" disabled>Välj systemroll</option>

                {/* Super-admin kan sätta super-admin */}
                {isEditingSuperAdmin && (
                  <option value="super-admin">Super-Admin</option>
                )}

                {/* Endast super-admin får se Admin */}
                {isSuperAdmin && (
                  <option value="admin">Admin</option>
                )}

                <option value="scheduler">Schemaläggare</option>
                <option value="payroll-manager">Lönehanterare</option>
                <option value="viewer">Granskare</option>
                <option value="user">Användare</option>
              </select>


              <input
                type="text"
                value={newEmployeeTitle}
                onChange={e => setNewEmployeeTitle(e.target.value)}
                placeholder="Arbetsroll (valfri, t.ex. Värd, Väktare, Kock)"
                style={{ padding: 10, borderRadius: 6, border: '1px solid #D1D5DB' }}
              />

              {/* Personnummer (valfritt) – realtidsformattering */}
              <input
                type="text"
                value={newEmployeePersonalNumber}
                onChange={e => setNewEmployeePersonalNumber(e.target.value)}
                placeholder="Personnummer (t.ex. 19900101-1234 eller 900101-1234)"
                style={{ padding: 10, borderRadius: 6, border: '1px solid #D1D5DB' }}
              />

              <div className="form-buttons" style={{ display: 'flex', gap: 10, marginTop: 12, width: '100%' }}>
                <button
                  type="submit"
                  style={{ flex: 1, padding: '12px 18px', borderRadius: 8, border: 'none', background: '#4facfe', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.15)', transition: 'all 0.2s ease-in-out' }}
                  onMouseOver={e => e.currentTarget.style.filter = 'brightness(1.1)'}
                  onMouseOut={e => e.currentTarget.style.filter = 'brightness(1)'}
                  disabled={formSubmitting}
                >
                  {formSubmitting
                    ? (editEmployeeState ? 'Sparar...' : 'Skickar...')
                    : (editEmployeeState ? 'Spara Ändringar' : 'Lägg till')}
                </button>
                <button
                  type="button"
                  onClick={togglePopup}
                  style={{ flex: 1, padding: '12px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)', color: '#333', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'all 0.2s ease-in-out' }}
                  onMouseOver={e => e.currentTarget.style.filter = 'brightness(1.05)'}
                  onMouseOut={e => e.currentTarget.style.filter = 'brightness(1)'}
                >
                  Stäng
                </button>
              </div>

            </form>
          </div>
        </div>
      )} 

      <div className="employees-list">
        <div className="column-headers" style={gridStyle}>
          <p className="column-header name-header" style={{ ...smallText, fontWeight: 'bold' }}>Namn</p>
          <p className="column-header email-header" style={{ ...smallText, fontWeight: 'bold' }}>Email</p>
          <p className="column-header pn-header" style={{ ...smallText, fontWeight: 'bold' }}>Personnummer</p>
          <p className="column-header title-header" style={{ ...smallText, fontWeight: 'bold' }}>Arbetsroll</p>
          <p className="column-header hours-header" style={{ ...smallText, fontWeight: 'bold' }}>
            Timmar ({(() => {
              const month = new Date(selectedYear, selectedMonth - 1, 1)
                .toLocaleString('sv-SE', { month: 'short' })
                .replace('.', '');
              return month.charAt(0).toUpperCase() + month.slice(1);
            })()})
          </p>

          <p className="column-header role-header" style={{ ...smallText, fontWeight: 'bold' }}>Systemroll</p>
          <div className="column-action-placeholder"></div>
        </div>

        {payLoading ? <Spinner /> : (
          <div>
            {filteredEmployees.map(emp => {
              const expiresAt = emp.invitationExpiresAt ? new Date(emp.invitationExpiresAt) : null;
              const isPending = emp.membershipStatus === "pending" && (!expiresAt || expiresAt.getTime() > Date.now());
              const isExpired = emp.membershipStatus === "pending" && !!expiresAt && expiresAt.getTime() <= Date.now();
              const hours = workedHoursMap[emp._id] || "0.00";
              const isInactive = emp.isActive === false;
              const isSelf = emp._id === user._id;

              const isReminderLoading = loadingReminderId === emp._id;
              const isResendLoading = loadingResendId === emp._id;

              return (
                <div
                  className="employee-row"
                  key={emp._id}
                  style={{
                    ...gridStyle,
                    backgroundColor: isInactive ? "#f5f5f5" : "",
                    borderLeft: isExpired
                      ? '3px solid #ff4d4d'
                      : isInactive
                        ? '3px solid #ccc'
                        : isPending
                          ? '3px solid #f7d358'
                          : '',
                    backgroundColor: isExpired
                      ? '#ffe6e6'
                      : isPending
                        ? '#fff5cc'
                        : ''
                  }}
                >
                  <p className="employee-name" style={{ ...smallText, color: isInactive ? "grey" : "black" }}>
                    {emp.name}
                    {isInactive && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>
                        (Inaktiv)
                      </span>
                    )}
                  </p>
                  <p className="employee-email"style={{ ...smallText, color: isInactive ? "grey" : "black" }} >{emp.email}</p>
                  <p className="employee-pn" style={{ ...smallText, color: isInactive ? "grey" : "black" }}>{emp.personalNumber || ""}</p>
                  <p className="employee-title" style={{ ...smallText, color: isInactive ? "grey" : "black" }}>{emp.title || ""}</p>
                  <p className="employee-hours" style={{ ...smallText, color: isInactive ? "grey" : "black" }}>{hours}</p>
                  <p className="employee-role" style={{ ...smallText, color: isInactive ? "grey" : "black" }}>
                    {ROLE_LABELS_SV[emp.role] || emp.role}
                  </p>


                  {(user.role === "super-admin" ||
                    (user.role === "admin" && emp.role === "user")) && (
                    <div
                      className={`employee-actions ${activeMenu === emp._id ? "active" : ""}`}
                      ref={activeMenu === emp._id ? menuRef : null}
                    >
                      {canEditEmployees && (
                        <button
                          className="menu-button"
                          onClick={() => toggleMenu(emp._id)}
                        >
                          ⋮
                        </button>
                      )}
                      {activeMenu === emp._id && (
                        // Keep original menu look via CSS, only enforce width
                        <div className="action-menu" style={{ minWidth: 180 }}>
                          {isExpired ? (
                            <button
                              onClick={() => openResendModal(emp)}
                              disabled={isResendLoading || isReminderLoading}
                            >
                              {isResendLoading && <InlineSpinner />} Skicka om inbjudan
                            </button>
                          ) : isPending ? (
                            <button
                              onClick={() => handleSendReminder(emp)}
                              disabled={isReminderLoading || isResendLoading}
                            >
                              {isReminderLoading && <InlineSpinner />} Skicka påminnelse
                            </button>
                          ) : null}

                        <button onClick={() => handleEdit(emp)}>Redigera</button>
                        {isInactive ? (
                          <button
                            onClick={() => handleReactivate(emp)}
                            disabled={reactivateLoadingId === emp._id}
                          >
                            {reactivateLoadingId === emp._id && <InlineSpinner />}
                            Återaktivera
                          </button>
                        ) : (
                          !isSelf &&
                          emp.role !== "super-admin" && (
                            <button
                              onClick={() =>
                                openDeactivateModal(emp)
                              }
                            >
                              Inaktivera konto
                            </button>
                          )
                        )}

                        {emp.role !== "super-admin" && (
                          <button
                            onClick={() => openDeleteModal(emp)}
                          >
                            Radera
                          </button>
                        )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {resendModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: '8px', padding: '24px',
            maxWidth: '400px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }}>
            <p>Vill du skicka om inbjudan till <strong>{employeeToResend?.name}</strong>?</p>
            <div style={{ marginTop: '20px' }}>
              <button style={{
                padding: '8px 16px', margin: '0 8px',
                border: 'none', borderRadius: '4px',
                cursor: 'pointer', fontSize: '14px',
                backgroundColor: '#5DADE2',
                color: '#000'
              }} onClick={confirmResend}>Ja</button>
              <button style={{
                padding: '8px 16px', margin: '0 8px',
                border: 'none', borderRadius: '4px',
                cursor: 'pointer', fontSize: '14px',
                backgroundColor: '#e0e0e0',
                color: '#333'
              }} onClick={closeResendModal}>Avbryt</button>
            </div>
          </div>
        </div>
      )}


      {deleteModalOpen && (
        
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: '8px', padding: '24px',
            maxWidth: '400px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }}>
            <p>Är du säker på att du vill radera <strong>{employeeToDelete?.name}</strong>?</p>
            <p style={{fontSize: 11, color: "grey"}}>Personen kommer att raderas permanent och all existerande data kommer att förloras.</p>

            <input
              type="text"
              placeholder={`Skriv "${employeeToDelete?.name}" för att bekräfta`}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              style={{
                marginTop: 12,
                padding: 8,
                width: '100%',
                borderRadius: 6,

                border:
                  deleteConfirmText &&
                  deleteConfirmText !== employeeToDelete?.name
                    ? '1px solid red'
                    : '1px solid #ccc',

                outline: 'none', // 👈 tar bort default blå focus

                boxShadow:
                  deleteConfirmText &&
                  deleteConfirmText !== employeeToDelete?.name
                    ? '0 0 0 1px red'
                    : 'none'
              }}
            />

            <div style={{ marginTop: '20px' }}>
              <button
                onClick={confirmDelete}
                disabled={deletingEmployee || deleteConfirmText !== employeeToDelete?.name}
                style={{
                  padding: '8px 16px',
                  margin: '0 8px',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',

                  // 👇 KEY PART
                  backgroundColor:
                    (deletingEmployee || deleteConfirmText !== employeeToDelete?.name)
                      ? '#f5b5b5'
                      : '#e53935',

                  color: '#fff',
                  cursor:
                    (deletingEmployee || deleteConfirmText !== employeeToDelete?.name)
                      ? 'not-allowed'
                      : 'pointer',

                  opacity:
                    (deletingEmployee || deleteConfirmText !== employeeToDelete?.name)
                      ? 0.6
                      : 1,

                  transition: 'all 0.2s ease'
                }}
              >
                {deletingEmployee ? 'Raderar...' : 'Radera'}
              </button>
              <button style={{
                padding: '8px 16px', margin: '0 8px',
                border: 'none', borderRadius: '4px',
                cursor: 'pointer', fontSize: '14px',
                backgroundColor: '#e0e0e0',
                color: '#333'
              }} onClick={closeDeleteModal} disabled={deletingEmployee}>Avbryt</button>
            </div>
          </div>
        </div>
      )}

      {deactivateModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', borderRadius: '8px', padding: '24px',
            maxWidth: '400px', width: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }}>
            <p>Vill du Inaktivera <strong>{employeeToDeactivate?.name}</strong>?</p>
            <p style={{fontSize: 11, color: "grey"}}>Personen kommer förlora åtkomst till sitt konto men all existerande data kommer att behållas.</p>
            <div style={{ marginTop: '20px' }}>
              <button
                style={{
                  padding: '8px 16px',
                  margin: '0 8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: 'red',
                  color: '#fff',
                  cursor: 'pointer'
                }}
                onClick={confirmDeactivate}
                disabled={deactivatingEmployee}
              >
                <p style={{fontWeight: 700}}>{deactivatingEmployee ? 'Inaktiverar...' : 'Inaktivera'}</p>
              </button>

              <button
                style={{
                  padding: '8px 16px',
                  margin: '0 8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#e0e0e0',
                  cursor: 'pointer'
                }}
                onClick={closeDeactivateModal}
                disabled={deactivatingEmployee}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}




      <ImportEmployeesPopup
        visible={showImportEmployeesPopup}
        onClose={() =>
          setShowImportEmployeesPopup(false)
        }
        onImport={async ({
          parsedRows,
          columnMapping,
          roleValueMapping,
        }) => {
          try {
            const { data } = await axios.post(
              `${BACKEND_URL}/import-employees`,
              {
                currentUserId: user._id,
                companyId: company._id,

                parsedRows,
                columnMapping,
                roleValueMapping,
              }
            );

            if (data.status === "success") {
              setEmployees(data.employees);

              setToastType("success");
              setToastMessage(
                `${data.importedCount} anställda importerade`
              );

              setShowImportEmployeesPopup(false);
            } else {
              setToastType("error");
              setToastMessage(
                data.errorText ||
                  "Import misslyckades"
              );
            }
          } catch (err) {
            console.error(err);

            setToastType("error");
            setToastMessage(
              "Kunde inte importera anställda"
            );
          }
        }}
      />

      <Toast
        message={toastMessage}
        type={toastType}
        onClose={() => setToastMessage("")}
      />
    </div>
  );
};

export default Employees;
